import { useEffect, useRef } from "react";

/**
 * Plays a short 2-note chime via Web Audio API + vibrates + browser notification.
 * No external audio file needed.
 */
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const play = (freq, start, duration = 0.22) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.35, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + duration);
    };
    play(880, 0); // A5
    play(1318.5, 0.22); // E6 — pleasant rising chime
    setTimeout(() => ctx.close(), 1200);
  } catch {}
}

function vibrate() {
  try { navigator.vibrate && navigator.vibrate([180, 80, 180, 80, 300]); } catch {}
}

function notify(title, body) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico" });
    }
  } catch {}
}

/**
 * Fires (once) when the ticket transitions from 'waiting' → 'called'.
 */
export function useTicketNotification(ticket) {
  const lastStatusRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!ticket) {
      lastStatusRef.current = null;
      firedRef.current = false;
      return;
    }
    // Ask permission proactively once the visitor has a waiting ticket
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
    const prev = lastStatusRef.current;
    lastStatusRef.current = ticket.status;
    if (!firedRef.current && prev === "waiting" && ticket.status === "called") {
      firedRef.current = true;
      playChime();
      vibrate();
      notify("C'est à vous !", `Ticket n°${ticket.number} appelé chez ${ticket.commerceName || ""}`);
    }
    // Reset fire flag if the ticket changes to a new one
    if (ticket.status === "waiting") firedRef.current = false;
  }, [ticket?.id, ticket?.status, ticket?.number, ticket?.commerceName, ticket]);
}
